import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { Button } from "@/components/ui/baseUi/button"
import { Separator } from "@/components/ui/baseUi/separator"
import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { ArrowIcon } from "@images/svgIcons/arrowIcon"
import { PencilSolidIcon } from "@images/svgIcons/essentials/pencilSolidIcon"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import {
  BoxStaffPermissionsModal,
  type BoxStaffPermissionsModalRef,
} from "@modules/boxStaff/components/boxStaffPermissionsModal"
import { BoxStaffRoleModal, type BoxStaffRoleModalRef } from "@modules/boxStaff/components/boxStaffRoleModal"
import { BoxStaffDetailedEntity } from "@modules/boxStaff/entities/boxStaffEntity"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import {
  type BoxStaffDetailedUpadteInput,
  BoxStaffPermissions,
  BoxStaffRole,
} from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HuPermissions } from "@modules/ui/components/huPermissions"
import { type UseMutateAsyncFunction } from "@tanstack/react-query"
import { useRef } from "react"
import { z } from "zod"
import { LoaderCircleIcon } from "lucide-react"

type BoxStaffTabDayToDayProps = {
  staffDetailed?: BoxStaffDetailedEntity
  isLoading: boolean
  updateBoxStaff: UseMutateAsyncFunction<void, Error, BoxStaffDetailedUpadteInput>
} & React.ComponentProps<"div">

export function BoxStaffTabDayToDay({ staffDetailed, isLoading, updateBoxStaff, ...props }: BoxStaffTabDayToDayProps) {
  const { hideSidebar } = useSidebar()
  const boxStaffPermissionsModal = useRef<BoxStaffPermissionsModalRef>(null)
  const boxStaffRoleModal = useRef<BoxStaffRoleModalRef>(null)
  // PERMISSIONS - Head-coach & Coach & Financial permissions denied
  const canChangeRoleStaff = useBoxStaffPermission(BoxStaffPermissions.CHANGE_ROLE_STAFF)

  const handlePermessions = () => {
    boxStaffPermissionsModal.current?.open()
  }

  const handleChangeRole = () => {
    boxStaffRoleModal.current?.open()
  }

  const boxStaffDayToDaySchema = z.object({
    internalNote: z.string().refine((value) => value !== (staffDetailed?.internalNote ?? ""), {
      message: word("box.staff.table.dayToDay.internalNote.error"),
    }),
  })

  const form = useAppForm({
    defaultValues: { internalNote: staffDetailed?.internalNote ?? "" },
    validators: {
      onChange: boxStaffDayToDaySchema,
    },
    onSubmit: async ({ value }) => {
      await updateBoxStaff({ internalNote: value.internalNote })
        .then(() => {
          toast.success(word("box.staff.table.dayToDay.toast.success"), {
            classNames: successToastClassName(),
          })
        })
        .catch(() => {
          toast.error(word("box.staff.table.dayToDay.toast.error"), {
            classNames: errorToastClassName(),
          })
        })
    },
  })

  return (
    <>
      <div className={cn("flex w-full flex-col gap-8", !canChangeRoleStaff && "mb-8")} {...props}>
        {/* INTERNAL NOTE*/}
        <div className="mt-2 flex flex-col gap-2">
          <Text fontStyle="TM" className="text-textDefault-body">
            {word("box.staff.table.dayToDay.internalNote.label")}
          </Text>
          <div className="mt-2 mb-2 flex flex-row gap-2">
            <WarningIcon color="currentColor" width={24} height={24} className="w-1/12 text-textDefault-body" />
            <Text fontStyle="BS" className="text-textDefault-placeholder">
              {word("box.staff.table.dayToDay.internalNote.warning")}
            </Text>
          </div>
          {isLoading ? (
            <Skeleton className="h-28 rounded-lg" />
          ) : (
            <form.AppField name="internalNote">
              {(field) => (
                <field.Field>
                  <field.Content>
                    <field.Textarea
                      disabled={!canChangeRoleStaff}
                      placeholder={word("box.staff.table.dayToDay.internalNote.placeholder")}
                      className="field-sizing-fixed"
                      rows={5}
                    />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>
          )}
        </div>
        {/* PERMISSIONS */}
        <HuPermissions
          title={word("box.staff.table.dayToDay.permissions.title")}
          role={staffDetailed?.role}
          button={
            <Button
              variant="underline"
              size="lg"
              onClick={() => {
                handlePermessions()
              }}
              className="ml-auto"
            >
              {word("box.staff.table.dayToDay.button.seeDetails")}
              <ArrowIcon color="currentColor" width={14} height={14} arrowDirection={"right"} />
            </Button>
          }
        />
        {/* CHANGE ROLE */}
        <div className="flex items-center gap-4">
          {isLoading || !staffDetailed?.role ? (
            <Skeleton className="h-8 w-28 rounded-lg" />
          ) : (
            <Text fontStyle="LL" className="text-textDefault-body">
              {displayRole(staffDetailed.role)}
            </Text>
          )}
          <Separator orientation="horizontal" className="flex-1" />
          {isLoading ? (
            <Skeleton className="h-8 w-28 rounded-lg" />
          ) : staffDetailed ? (
            staffDetailed.role === BoxStaffRole.OWNER ? (
              <Text fontStyle="LS" className="w-max text-textDefault-placeholder">
                {word("box.staff.table.dayToDay.button.ownerCanChangeRole")}
              </Text>
            ) : canChangeRoleStaff ? (
              <Button
                variant="outline"
                onClick={() => {
                  handleChangeRole()
                }}
                className="flex items-center gap-2"
              >
                {word("box.staff.table.dayToDay.button.changeRole")}
                <PencilSolidIcon className={"text-textDefault-body"} width={14} height={14} color="currentColor" />
              </Button>
            ) : null
          ) : null}
        </div>
        {/* PERMISSIONS - Head-coach & Coach & Financial permissions denied */}
        {canChangeRoleStaff && (
          // BUTTON
          <form.Subscribe selector={(s) => [s.isSubmitting, s.isDirty, s.canSubmit]}>
            {([isSubmitting, isDirty, canSubmit]) => (
              <div className="mb-6 flex w-full flex-row items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    hideSidebar()
                  }}
                  className="grow"
                >
                  {word("global.cancel")}
                </Button>

                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => void form.handleSubmit()}
                  disabled={!isDirty || !canSubmit || isLoading || isSubmitting}
                  className="grow"
                  isLoading={isSubmitting}
                >
                  {isSubmitting && <LoaderCircleIcon className="animate-spin" />}
                  {word("global.save")}
                </Button>
              </div>
            )}
          </form.Subscribe>
        )}
      </div>
      <BoxStaffPermissionsModal ref={boxStaffPermissionsModal} />
      {staffDetailed?.role && <BoxStaffRoleModal staffDetailed={staffDetailed} ref={boxStaffRoleModal} />}
    </>
  )
}
